import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import ViewQuiltRoundedIcon from "@mui/icons-material/ViewQuiltRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";

const TAB_DEFINITIONS = Object.freeze({
  0: { label: "Diseño general", sectionLabel: "Diseño general" },
  1: { label: "Imágenes", sectionLabel: "Imágenes" },
  2: { label: "Secciones", sectionLabel: "Secciones de la página" },
  3: { label: "Datos del negocio", sectionLabel: "Datos del negocio" },
  4: { label: "SEO y publicación", sectionLabel: "SEO y publicación" },
});

function tabsForMode(mode) {
  if (mode === "sections") return [2];
  if (mode === "identity") return [3];
  if (mode === "legacy") return [0, 1, 2, 3, 4];
  return [0, 4];
}

function dialogTitle(mode) {
  if (mode === "sections") return "Secciones de la página";
  if (mode === "identity") return "Datos del negocio";
  if (mode === "legacy") return "Configuración de la página";
  return "Preferencias del sitio";
}

function dialogDescription(mode) {
  if (mode === "sections") return "Agregá, mostrá u ocultá bloques sin mezclar esta tarea con la configuración general.";
  if (mode === "identity") return "Estos datos se reutilizan donde corresponde en contacto, pie, buscadores y otras vistas públicas.";
  if (mode === "legacy") return "Ajustes generales y contenido de la página.";
  return "Ajustes globales o avanzados que afectan al sitio completo. El contenido de cada sección se edita desde su inspector.";
}

function clone(value) {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function normalizePublicStatus(value) {
  const candidate = String(value || "").trim().toLowerCase();
  return ["draft", "published", "maintenance"].includes(candidate)
    ? candidate
    : "draft";
}

function publicStatusLabel(value) {
  const status = normalizePublicStatus(value);
  if (status === "published") return "Publicado";
  if (status === "maintenance") return "Mantenimiento";
  return "Borrador";
}

function withoutPublicStatus(value) {
  const next = clone(value || {});
  delete next.publicStatus;
  return next;
}

function statusConfirmationMessage(status) {
  if (status === "published") {
    return "Se guardará el borrador pendiente y se publicará su última revisión real. La web pública pasará a mostrar esa versión.";
  }
  if (status === "maintenance") {
    return "La web pública mostrará la página de mantenimiento usando la última versión publicada. El borrador y el historial no se perderán.";
  }
  return "La web pública quedará oculta como borrador. La última versión publicada y todo el historial se conservarán.";
}

function getAtPath(source, path, fallback = "") {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => value?.[/^\d+$/.test(key) ? Number(key) : key], source) ?? fallback;
}

function setAtPath(source, path, value) {
  const next = clone(source);
  const keys = String(path || "").split(".").filter(Boolean);
  let cursor = next;
  keys.forEach((rawKey, index) => {
    const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    const nextKey = /^\d+$/.test(keys[index + 1]) ? Number(keys[index + 1]) : keys[index + 1];
    if (cursor[key] === undefined || cursor[key] === null) cursor[key] = typeof nextKey === "number" ? [] : {};
    cursor = cursor[key];
  });
  return next;
}

function Field({ form, path, label, onChange, multiline = false, minRows = 1, type = "text", helperText = "" }) {
  return (
    <TextField
      fullWidth
      type={type}
      label={label}
      value={getAtPath(form, path, "")}
      onChange={(event) => onChange(path, event.target.value)}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      helperText={helperText}
    />
  );
}

const GLOBAL_COLOR_TOKEN_PATHS = Object.freeze({
  "theme.primaryColor": "designTokens.colors.primary",
  "theme.secondaryColor": "designTokens.colors.secondary",
  "theme.surfaceColor": "designTokens.colors.surface",
  "theme.backgroundColor": "designTokens.colors.background",
  "theme.textColor": "designTokens.colors.text",
  "theme.mutedTextColor": "designTokens.colors.mutedText",
  "theme.dividerColor": "designTokens.colors.divider",
});

function normalizedHexColor(value) {
  const candidate = String(value || "").trim();
  if (/^#[0-9a-f]{6}$/i.test(candidate)) return candidate.toUpperCase();
  if (/^#[0-9a-f]{3}$/i.test(candidate)) {
    return `#${candidate.slice(1).split("").map((char) => char + char).join("")}`.toUpperCase();
  }
  const rgb = candidate.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) return `#${rgb.slice(1, 4).map((part) => Math.max(0, Math.min(255, Number(part))).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
  return "#000000";
}

function ColorField({ form, path, label, onChange }) {
  const value = getAtPath(form, path, "#000000") || "#000000";
  const hex = normalizedHexColor(value);
  return (
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Box component="input" type="color" value={hex} onChange={(event) => onChange(path, event.target.value)} sx={{ width: 48, height: 44, p: 0, border: 0, bgcolor: "transparent", cursor: "pointer" }} />
      <TextField
        fullWidth
        label={label}
        value={value}
        onFocus={(event) => event.target.select()}
        onChange={(event) => onChange(path, event.target.value)}
        helperText={`HEX actual: ${hex}`}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={`Copiar ${hex}`}>
                <IconButton size="small" edge="end" aria-label={`Copiar ${label}`} onClick={() => navigator.clipboard?.writeText(hex)}>
                  <ContentCopyRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  );
}

const SECTION_GROUPS = Object.freeze([
  {
    id: "structure",
    title: "Estructura",
    description: "Inicio y navegación.",
  },
  {
    id: "commerce",
    title: "Productos y servicios",
    description: "Bloques comerciales del mismo catálogo.",
  },
  {
    id: "content",
    title: "Contenido",
    description: "Historia, beneficios y contacto.",
  },
  {
    id: "closing",
    title: "Cierre y accesos",
    description: "Final de página y acciones flotantes.",
  },
]);

function PreviewLine({ width, active, height = 6, opacity = 1 }) {
  return (
    <Box
      sx={{
        width,
        height,
        borderRadius: 99,
        bgcolor: active ? "#F35A3A" : "rgba(25,36,47,.34)",
        opacity,
      }}
    />
  );
}

function PreviewCard({ active, height = 42, children }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        height,
        borderRadius: 1.5,
        bgcolor: active ? "rgba(243,90,58,.10)" : "rgba(25,36,47,.05)",
        border: "1px solid",
        borderColor: active ? "rgba(243,90,58,.20)" : "rgba(25,36,47,.09)",
        display: "grid",
        placeItems: "center",
      }}
    >
      {children}
    </Box>
  );
}

function SegmentMiniPreview({ type = "cards", active = false }) {
  const accent = active ? "#F35A3A" : "rgba(25,36,47,.38)";
  const soft = active ? "rgba(243,90,58,.10)" : "rgba(25,36,47,.05)";

  if (type === "navigation") {
    return (
      <Stack spacing={1.3} sx={{ width: "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <PreviewLine width="27%" active={active} height={9} />
          <Stack direction="row" spacing={0.65}>
            {[0, 1, 2].map((item) => <PreviewLine key={item} width={16} active={active} height={4} opacity={0.68} />)}
          </Stack>
        </Stack>
        <Box sx={{ height: 52, borderRadius: 1.8, bgcolor: soft, border: "1px solid rgba(25,36,47,.06)" }} />
      </Stack>
    );
  }

  if (type === "hero") {
    return (
      <Stack direction="row" spacing={1.4} sx={{ width: "100%", alignItems: "center" }}>
        <Stack spacing={0.85} sx={{ flex: 1.1 }}>
          <PreviewLine width="64%" active={active} height={10} />
          <PreviewLine width="90%" active={active} opacity={0.48} />
          <PreviewLine width="74%" active={active} opacity={0.34} />
          <Box sx={{ width: 38, height: 13, borderRadius: 99, bgcolor: accent, mt: 0.45 }} />
        </Stack>
        <Box sx={{ flex: 0.8, height: 72, borderRadius: 2, bgcolor: soft, border: "1px solid rgba(25,36,47,.07)" }} />
      </Stack>
    );
  }

  if (type === "split" || type === "contact") {
    return (
      <Stack direction="row" spacing={1.2} sx={{ width: "100%", alignItems: "stretch" }}>
        <PreviewCard active={active} height={72} />
        <Stack spacing={0.85} sx={{ flex: 1.15, justifyContent: "center" }}>
          <PreviewLine width="58%" active={active} height={9} />
          <PreviewLine width="94%" active={active} opacity={0.5} />
          <PreviewLine width="80%" active={active} opacity={0.36} />
          {type === "contact" ? <PreviewLine width="46%" active={active} height={10} opacity={0.78} /> : null}
        </Stack>
      </Stack>
    );
  }

  if (type === "footer") {
    return (
      <Stack spacing={1.2} sx={{ width: "100%", justifyContent: "flex-end" }}>
        <Stack direction="row" spacing={1}>
          {[0, 1, 2].map((item) => (
            <Stack key={item} spacing={0.65} sx={{ flex: 1 }}>
              <PreviewLine width={item === 0 ? "70%" : "52%"} active={active} height={6} opacity={0.72} />
              <PreviewLine width="90%" active={active} height={4} opacity={0.34} />
              <PreviewLine width="72%" active={active} height={4} opacity={0.26} />
            </Stack>
          ))}
        </Stack>
        <Box sx={{ height: 18, borderRadius: 1.2, bgcolor: soft }} />
      </Stack>
    );
  }

  if (type === "floating") {
    return (
      <Box sx={{ width: "100%", height: 82, position: "relative", borderRadius: 2, bgcolor: soft, border: "1px solid rgba(25,36,47,.06)" }}>
        <Stack spacing={0.7} sx={{ position: "absolute", left: 12, top: 15, width: "60%" }}>
          <PreviewLine width="72%" active={active} opacity={0.28} />
          <PreviewLine width="92%" active={active} opacity={0.2} />
        </Stack>
        <Box sx={{ position: "absolute", right: 10, bottom: 10, width: 30, height: 30, borderRadius: "50%", bgcolor: accent, boxShadow: "0 4px 12px rgba(25,36,47,.14)" }} />
      </Box>
    );
  }

  if (type === "promo") {
    return (
      <Stack spacing={1.1} sx={{ width: "100%" }}>
        <Box sx={{ height: 34, borderRadius: 1.8, bgcolor: accent, opacity: active ? 0.84 : 0.48 }} />
        <Stack direction="row" spacing={0.9}>
          {[0, 1, 2].map((item) => <PreviewCard key={item} active={active} height={35} />)}
        </Stack>
      </Stack>
    );
  }

  if (type === "logos") {
    return (
      <Stack spacing={1.25} sx={{ width: "100%" }}>
        <PreviewLine width="42%" active={active} height={8} opacity={0.62} />
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between" }}>
          {[0, 1, 2, 3].map((item) => (
            <Box key={item} sx={{ width: 31, height: 31, borderRadius: item % 2 ? 1.4 : "50%", bgcolor: item % 2 ? soft : accent, opacity: item % 2 ? 1 : 0.48 }} />
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.1} sx={{ width: "100%" }}>
      <PreviewLine width={type === "benefits" ? "46%" : "58%"} active={active} height={9} opacity={0.68} />
      <Stack direction="row" spacing={0.9}>
        {[0, 1, 2].map((item) => (
          <PreviewCard key={item} active={active} height={type === "categories" ? 48 : 58}>
            {type === "benefits" ? <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: accent, opacity: 0.62 }} /> : null}
          </PreviewCard>
        ))}
      </Stack>
    </Stack>
  );
}

function SegmentChoiceCard({ definition, active, disabled, onToggle }) {
  const presentation = definition.presentation || {};
  return (
    <Box
      sx={{
        minWidth: 0,
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: active ? "rgba(243,90,58,.40)" : "rgba(25,36,47,.11)",
        bgcolor: active ? "rgba(243,90,58,.035)" : "#fff",
        boxShadow: active ? "0 10px 26px rgba(243,90,58,.08)" : "0 6px 18px rgba(25,36,47,.045)",
        opacity: disabled ? 0.6 : 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "border-color .16s ease, box-shadow .16s ease, transform .16s ease",
        "&:hover": disabled ? undefined : {
          borderColor: active ? "rgba(243,90,58,.50)" : "rgba(25,36,47,.20)",
          boxShadow: "0 12px 28px rgba(25,36,47,.08)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          minHeight: 126,
          px: 2,
          py: 1.7,
          bgcolor: active ? "rgba(243,90,58,.045)" : "#F7F8FA",
          borderBottom: "1px solid rgba(25,36,47,.07)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <SegmentMiniPreview type={presentation.preview} active={active} />
      </Box>

      <Stack spacing={1.15} sx={{ p: 1.8, flex: 1 }}>
        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 950, color: "#19242F", fontSize: "1rem" }}>{definition.label}</Typography>
          <Chip
            size="small"
            icon={active ? <CheckRoundedIcon /> : undefined}
            label={active ? "En la página" : "Disponible"}
            variant={active ? "filled" : "outlined"}
            color={active ? "primary" : "default"}
            sx={{ height: 24, fontWeight: 850 }}
          />
        </Stack>

        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.74)", lineHeight: 1.45 }}>
          {presentation.summary}
        </Typography>

        <Stack spacing={0.7} sx={{ mt: "auto !important" }}>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <PlaceRoundedIcon sx={{ fontSize: 16, color: "rgba(25,36,47,.48)" }} />
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", lineHeight: 1.3 }}>
              {presentation.placement}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <ViewQuiltRoundedIcon sx={{ fontSize: 16, color: "rgba(25,36,47,.48)" }} />
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", lineHeight: 1.3 }}>
              {presentation.visualHint}
            </Typography>
          </Stack>
        </Stack>

        <Button
          fullWidth
          size="small"
          variant={active ? "outlined" : "contained"}
          startIcon={active ? <RemoveRoundedIcon /> : <AddRoundedIcon />}
          disabled={disabled}
          onClick={() => onToggle(!active)}
          sx={{ mt: 0.35, minHeight: 44, textTransform: "none", fontWeight: 900, borderRadius: 2 }}
        >
          {active ? "Quitar de la página" : "Agregar a la página"}
        </Button>
      </Stack>
    </Box>
  );
}

export {
  TAB_DEFINITIONS,
  tabsForMode,
  dialogTitle,
  dialogDescription,
  clone,
  normalizePublicStatus,
  publicStatusLabel,
  withoutPublicStatus,
  statusConfirmationMessage,
  getAtPath,
  setAtPath,
  Field,
  GLOBAL_COLOR_TOKEN_PATHS,
  normalizedHexColor,
  ColorField,
  SECTION_GROUPS,
  PreviewLine,
  PreviewCard,
  SegmentMiniPreview,
  SegmentChoiceCard
};
